import { useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAspDetail, MsrAspZeile } from './typen';
import { ASP_ART, z } from './gemeinsam';
import { AspDetail } from './AspDetail';
import { RANG_SEITE, msrZiele, useMsrExport } from './kopf';
import {
  DataTable, EmptyState, ErrorState, FilterBar, StatusBadge,
  type FilterDefinition, type FilterWerte, type Spalte,
} from '../../muster';

/**
 * Reiter „Schaltschränke / ASPs" — Tabelle mit Suche, Sortierung, Filter
 * Art/Geschoss, Detail unter der Tabelle bei Klick.
 *
 * Umzug U6c: Tabelle auf `DataTable` (Sortierung, Suche, Spaltenwahl und
 * CSV kommen von dort), Filter auf `FilterBar`. Die eigene Sortierlogik
 * entfällt — `DataTable` sortiert deutsch und stellt fehlende Werte ans
 * Ende, was die alte `localeCompare`-Zeile nicht tat.
 */
export function Asps({ api, projektId, asps, kabelBestaetigt = false }: {
  api: ApiClient; projektId: string; asps: MsrAspZeile[];
  /** MSR-15: ob die Annahmenliste das Kabelkennzeichen als bestätigt führt (an `AspDetail`). */
  kabelBestaetigt?: boolean;
}) {
  const [werte, setWerte] = useState<FilterWerte>({ art: '', geschoss: '' });
  const [gewaehltId, setGewaehltId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MsrAspDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useMsrExport(RANG_SEITE, () => [
    msrZiele.aspListe(api, projektId), msrZiele.kabel(api, projektId), msrZiele.schrankbilanz(api, projektId),
  ], [api, projektId]);

  const geschosse = useMemo(() => Array.from(new Set(
    asps.map((a) => a.geschoss).filter((g): g is string => !!g))).sort(), [asps]);

  const filter: FilterDefinition[] = useMemo(() => [
    {
      schluessel: 'art', beschriftung: 'Art', art: 'auswahl',
      optionen: Object.entries(ASP_ART).map(([k, v]) => ({ wert: k, beschriftung: v })),
    },
    {
      schluessel: 'geschoss', beschriftung: 'Geschoss', art: 'auswahl',
      optionen: geschosse.map((g) => ({ wert: g, beschriftung: g })),
    },
  ], [geschosse]);

  const gefiltert = useMemo(() => asps
    .filter((a) => !werte.art || a.art === werte.art)
    .filter((a) => !werte.geschoss || a.geschoss === werte.geschoss),
  [asps, werte]);

  const detailNeuLaden = (id: string) => {
    setFehler(null);
    api.msrAsp(projektId, id).then(setDetail)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => {
    if (gewaehltId) detailNeuLaden(gewaehltId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gewaehltId]);

  const artZahl = (a: MsrAspZeile, k: keyof MsrAspZeile['belegt']) =>
    `${z(a.belegt[k])}/${z(a.kapazitaet[k])}`;

  // Schrankfelder (Spezifikation Abschnitt 16) — „1 + 2" (Modell + Erweiterung),
  // solange die Kernableitung die Aufteilung noch nicht liefert bleibt es bei
  // der reinen Anzahl aus `felder` (kein „+ 0").
  const felderAnzeige = (a: MsrAspZeile) => {
    const modell = a.felderModell ?? a.felder ?? 0;
    const erweiterung = a.felderErweiterung ?? 0;
    if (erweiterung <= 0) return { text: String(modell), erweitert: false };
    return { text: `${modell} + ${erweiterung}`, erweitert: true };
  };

  const zaehlspalte = (k: keyof MsrAspZeile['belegt'], titel: string): Spalte<MsrAspZeile> => ({
    schluessel: k, titel, ausrichtung: 'rechts', breite: '74px',
    titelHinweis: `belegt / Kapazität (${titel})`,
    wert: (a) => a.belegt[k],
    inhalt: (a) => artZahl(a, k),
  });

  const spalten: Spalte<MsrAspZeile>[] = [
    { schluessel: 'kennung', titel: 'Kennung', wert: (a) => a.kennung, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (a) => ASP_ART[a.art] },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (a) => a.geschoss ?? null },
    { schluessel: 'raum', titel: 'Raum', wert: (a) => a.raumname ?? a.raumnummer ?? null },
    {
      schluessel: 'felder', titel: 'Felder', ausrichtung: 'rechts', breite: '90px',
      wert: (a) => felderAnzeige(a).text,
      inhalt: (a) => {
        const f = felderAnzeige(a);
        if (!f.erweitert) return <span title={a.felderBegruendung ?? undefined}>{f.text}</span>;
        const [modell, erweiterung] = f.text.split(' + ');
        return (
          <span title={a.felderBegruendung ?? undefined}>
            {modell}{' + '}
            <span style={{ color: 'var(--warnung)', fontWeight: 600 }}>{erweiterung}</span>
          </span>
        );
      },
    },
    { schluessel: 'einspeisung', titel: 'Einspeisung', wert: (a) => a.einspeisung ?? null },
    zaehlspalte('be', 'BE'),
    zaehlspalte('ba', 'BA'),
    zaehlspalte('ae', 'AE'),
    zaehlspalte('aa', 'AA'),
    zaehlspalte('ze', 'ZE'),
    zaehlspalte('kom', 'KOM'),
    {
      schluessel: 'auslastungProzent', titel: 'Auslastung', einheit: '%', art: 'zahl',
      breite: '130px', wert: (a) => a.auslastungProzent,
      inhalt: (a) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 40, height: 6, borderRadius: 2,
                          background: 'var(--flaeche-gedaempft)' }}>
            <span style={{ display: 'block', height: 6, borderRadius: 2,
              width: `${Math.min(100, a.auslastungProzent)}%`,
              background: a.ueberKapazitaet ? 'var(--schlecht)' : 'var(--akzent)' }} />
          </span>
          {z(a.auslastungProzent)}
        </span>
      ),
    },
    { schluessel: 'reserveProzent', titel: 'Reserve', einheit: '%', art: 'zahl',
      wert: (a) => a.reserveProzent },
    { schluessel: 'leistungW', titel: 'Leistung', einheit: 'W', art: 'zahl',
      wert: (a) => a.leistungW ?? null },
    {
      schluessel: 'zustand', titel: 'Kapazität', art: 'status', breite: '140px',
      wert: (a) => (a.ueberKapazitaet ? 'über Kapazität' : 'im Rahmen'),
      inhalt: (a) => (a.ueberKapazitaet
        ? <StatusBadge klein zustand="fehler" text="über Kapazität" />
        : <StatusBadge klein zustand="gut" text="im Rahmen" />),
    },
  ];

  return (
    <>
      <FilterBar
        filter={filter}
        werte={werte}
        beiAenderung={setWerte}
        inLeiste={2}
      />

      <DataTable
        name="Schaltschränke / ASPs"
        beschreibung="Automationsstationen mit Belegung, Reserve und Anschlussleistung."
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(a) => a.id}
        dicht
        ausfuhr
        ausfuhrDateiname="msr-asps"
        klebendeErsteSpalte
        beiZeileKlick={(a) => setGewaehltId(a.id)}
        aktiveZeile={gewaehltId ?? undefined}
        zeilenKlasse={(a) => (a.ueberKapazitaet ? 'datenfeld' : undefined)}
        leer={(
          <EmptyState
            titel={asps.length === 0
              ? 'Noch kein Schaltschrank abgeleitet.'
              : 'Kein Schaltschrank passt zu diesen Filtern.'}
            erklaerung={asps.length === 0
              ? 'ASPs entstehen aus dem Ableitungslauf über die aufgenommenen Gewerke.'
              : `${asps.length} Schaltschränke sind vorhanden, keiner erfüllt die gesetzten `
                + 'Filter.'}
            aktion={asps.length > 0 ? (
              <button type="button" className="m-knopf"
                onClick={() => setWerte({ art: '', geschoss: '' })}>
                Filter zurücksetzen
              </button>
            ) : undefined}
          />
        )}
      />

      {fehler && (
        <ErrorState
          meldung="Die Einzelheiten dieses Schaltschranks konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => gewaehltId && detailNeuLaden(gewaehltId)}
        />
      )}

      {gewaehltId && detail && (
        <AspDetail api={api} projektId={projektId} asp={detail} kabelBestaetigt={kabelBestaetigt}
          onGespeichert={() => detailNeuLaden(gewaehltId)} />
      )}
    </>
  );
}
